import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';

export interface UserFile {
  id: string;
  user_id: string;
  file_path: string;
  file_name: string;
  content: string | null;
  content_type: string;
  size_bytes: number;
  is_directory: boolean;
  parent_path: string | null;
  created_at: string;
  updated_at: string;
}

export const useUserVFS = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState<UserFile[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const repairedRef = useRef(false);

  const normalizePath = (p: string) => p.replace(/\//g, '\\').replace(/\\\\+/g, '\\');
  const osUserFiles = () => supabase.from('os_user_files') as any;

  // Load user files

  useEffect(() => {
    const ensureDirRecord = async (dirPath: string) => {
      const path = normalizePath(dirPath);
      const { error } = await osUserFiles()
        .upsert({
          user_id: user!.id,
          file_path: path,
          file_name: path.split('\\').pop() || path,
          content: null,
          content_type: 'directory',
          size_bytes: 0,
          is_directory: true,
          parent_path: path.includes('\\') ? path.substring(0, path.lastIndexOf('\\')) || null : null,
        }, {
          onConflict: 'user_id,file_path',
          ignoreDuplicates: true,
        });

      if (error) {
        console.error('Failed to ensure directory record:', error);
      }
    };

    const repairMisplaced = async (loaded: UserFile[]) => {
      if (!user || repairedRef.current) return;
      const toFix = loaded.filter(
        f => (f.file_name?.includes('/') || f.file_path?.includes('/'))
      );
      if (toFix.length === 0) return;

      for (const f of toFix) {
        const normalizedFullPath = normalizePath(f.file_path);
        const lastSep = normalizedFullPath.lastIndexOf('\\');
        const fileName = lastSep >= 0 ? normalizedFullPath.substring(lastSep + 1) : normalizedFullPath;
        const parent = lastSep >= 0 ? normalizedFullPath.substring(0, lastSep) : null;
        const duplicate = loaded.find(other => other.id !== f.id && normalizePath(other.file_path) === normalizedFullPath);

        if (duplicate) {
          const { error: deleteError } = await osUserFiles()
            .delete()

            .eq('user_id', user.id)
            .eq('id', f.id);

          if (deleteError) {
            console.error('Failed to remove duplicate misplaced file:', deleteError);
          }
          continue;
        }

        // Create any missing intermediate directories
        if (parent) {
          const segs = parent.split('\\');
          let current = segs[0];
          for (let i = 1; i < segs.length; i++) {
            current += `\\${segs[i]}`;
            await ensureDirRecord(current);
          }
        }

        const { error: updateError } = await osUserFiles()
          .update({

            file_path: normalizedFullPath,
            file_name: fileName,
            parent_path: parent,
          })
          .eq('user_id', user.id)
          .eq('id', f.id);

        if (updateError && updateError.code !== '23505') {
          console.error('Failed to repair misplaced file:', updateError);
        }
      }

      repairedRef.current = true;
    };

    const loadFiles = async () => {
      if (!user) {
        setFiles([]);
        setIsLoaded(true);
        return;
      }

      try {
        const { data: fileData, error } = await osUserFiles()
          .select('*')

          .eq('user_id', user.id)
          .order('file_path');

        if (error) throw error;

        const arr = fileData || [];
        await repairMisplaced(arr);

        const { data: refreshedData, error: refreshedError } = await osUserFiles()
          .select('*')

          .eq('user_id', user.id)
          .order('file_path');

        if (refreshedError) throw refreshedError;

        setFiles((refreshedData || []).map(f => ({
          ...f,
          file_path: normalizePath(f.file_path),
          parent_path: f.parent_path ? normalizePath(f.parent_path) : null,
          file_name: f.file_name?.includes('/') ? f.file_name.split('/').pop() || f.file_name : f.file_name
        })));
      } catch (error) {
        console.error('Failed to load user files:', error);
        setFiles([]);
      } finally {
        setIsLoaded(true);
      }
    };

    loadFiles();
  }, [user]);

  // Create file or directory
  const createFile = useCallback(async (
    filePath: string,
    content: string = '',
    isDirectory: boolean = false,
    contentType: string = 'text/plain'
  ) => {
    if (!user) {
      console.warn('Cannot create file: user not authenticated');
      return false;
    }

    const normalized = normalizePath(filePath);
    const fileName = normalized.split('\\').pop() || normalized;
    const parentPath = normalized.includes('\\') ? normalized.substring(0, normalized.lastIndexOf('\\')) || null : null;

    if (files.some(file => file.file_path === normalized)) {
      return true;
    }

    try {
      const { data, error } = await osUserFiles()
        .upsert({

          user_id: user.id,
          file_path: normalized,
          file_name: fileName,
          content: isDirectory ? null : content,
          content_type: isDirectory ? 'directory' : contentType,
          size_bytes: isDirectory ? 0 : content.length,
          is_directory: isDirectory,
          parent_path: parentPath
        }, {
          onConflict: 'user_id,file_path',
          ignoreDuplicates: true,
        })
        .select();

      if (error) throw error;

      let nextFile = data?.[0] ?? null;

      if (!nextFile) {
        const { data: existing, error: existingError } = await osUserFiles()
          .select('*')

          .eq('user_id', user.id)
          .eq('file_path', normalized)
          .maybeSingle();

        if (existingError) throw existingError;
        if (!existing) {
          throw new Error(`File record not found after upsert for path: ${normalized}`);
        }
        nextFile = existing;
      }

      setFiles(prev => {
        const normalizedFile = {
          ...nextFile,
          file_path: normalized,
          parent_path: nextFile.parent_path ? normalizePath(nextFile.parent_path) : parentPath,
          file_name: nextFile.file_name?.includes('/') ? nextFile.file_name.split('/').pop() || nextFile.file_name : nextFile.file_name
        };
        return prev.some(file => file.file_path === normalized)
          ? prev.map(file => file.file_path === normalized ? normalizedFile : file)
          : [...prev, normalizedFile];
      });
      return true;
    } catch (error) {
      console.error('Failed to create file:', error);
      return false;
    }
  }, [files, user]);

  // Update file content
  const updateFile = useCallback(async (filePath: string, content: string) => {
    if (!user) {
      console.warn('Cannot update file: user not authenticated');
      return false;
    }

    try {
      const normalized = normalizePath(filePath);
      const { data, error } = await osUserFiles()
        .update({

          content,
          size_bytes: content.length,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('file_path', normalized)
        .select()
        .single();

      if (error) throw error;

      setFiles(prev => prev.map(file => 
        file.file_path === normalized ? data : file
      ));
      
      return true;
    } catch (error) {
      console.error('Failed to update file:', error);
      return false;
    }
  }, [user]);

  // Delete file or directory
  const deleteFile = useCallback(async (filePath: string) => {
    if (!user) {
      console.warn('Cannot delete file: user not authenticated');
      return false;
    }

    try {
      const normalized = normalizePath(filePath);
      const { error } = await osUserFiles()
        .delete()

        .eq('user_id', user.id)
        .eq('file_path', normalized);

      if (error) throw error;

      setFiles(prev => prev.filter(file => file.file_path !== normalized));
      return true;
    } catch (error) {
      console.error('Failed to delete file:', error);
      return false;
    }
  }, [user]);

  // Get file by path
  const getFile = useCallback((filePath: string): UserFile | null => {
    const normalized = filePath.replace(/\//g, '\\').replace(/\\\\+/g, '\\');
    return files.find(file => file.file_path === normalized) || null;
  }, [files]);

  // NEW: Reload a single file from Supabase to ensure content is up-to-date
  const reloadFile = useCallback(async (filePath: string): Promise<UserFile | null> => {
    if (!user) {
      console.warn('Cannot reload file: user not authenticated');
      return null;
    }
    const normalized = normalizePath(filePath);
    const { data, error } = await osUserFiles()
      .select('*')

      .eq('user_id', user.id)
      .eq('file_path', normalized)
      .maybeSingle();

    if (error) {
      console.error('Failed to reload file:', error);
      return null;
    }

    if (!data) {
      return null;
    }

    const normalizedData: UserFile = {
      ...data,
      file_path: normalizePath(data.file_path),
      parent_path: data.parent_path ? normalizePath(data.parent_path) : null,
      file_name: data.file_name?.includes('/') ? data.file_name.split('/').pop() || data.file_name : data.file_name
    };

    setFiles(prev => {
      const others = prev.filter(f => f.file_path !== normalizedData.file_path);
      return [...others, normalizedData];
    });

    return normalizedData;
  }, [user]);

  // List files in directory
  const listFiles = useCallback((directoryPath: string): UserFile[] => {
    const normalized = directoryPath.replace(/\//g, '\\').replace(/\\\\+/g, '\\');
    return files.filter(file => file.parent_path === normalized);
  }, [files]);

  // Check if file exists
  const fileExists = useCallback((filePath: string): boolean => {
    const normalized = filePath.replace(/\//g, '\\').replace(/\\\\+/g, '\\');
    return files.some(file => file.file_path === normalized);
  }, [files]);

  return {
    files,
    isLoaded,
    createFile,
    updateFile,
    deleteFile,
    getFile,
    listFiles,
    fileExists,
    reloadFile
  };
};